import axios from 'axios';
import { API_BASE_URL } from './env';

// Create axios instance
export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Try both 'token' and 'accessToken' for backwards compatibility
    const token = localStorage.getItem('token') || localStorage.getItem('accessToken');
    console.log('API Request:', config.method?.toUpperCase(), config.url, {
      hasToken: !!token,
      tokenPreview: token ? token.substring(0, 20) + '...' : 'none'
    });
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Check if response is HTML instead of JSON (common when hitting wrong URL)
    if (error.response?.data && typeof error.response.data === 'string' && error.response.data.includes('<!DOCTYPE')) {
      console.error('API returned HTML instead of JSON. Check API URL configuration.');
      error.message = 'API configuration error';
      return Promise.reject(error);
    }

    // Handle both 401 (Unauthorized) and 403 (Forbidden) as auth errors
    if ((error.response?.status === 401 || error.response?.status === 403) && !originalRequest._retry) {
      originalRequest._retry = true;

      const errorMessage = error.response?.data?.error || '';

      // If token is malformed, invalid, expired, or forbidden, immediately redirect to login
      if (errorMessage.includes('Invalid token') || errorMessage.includes('malformed') ||
          errorMessage.includes('Access token required') || errorMessage.includes('expired') ||
          errorMessage.includes('Forbidden') || error.response?.status === 403) {
        console.warn('Invalid, expired, or forbidden token detected, redirecting to login...');
        localStorage.removeItem('token');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');

        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken,
          });

          const { accessToken, refreshToken: newRefreshToken } = response.data.tokens;
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('token', accessToken);
          localStorage.setItem('refreshToken', newRefreshToken);

          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        } else {
          // No refresh token, redirect to login
          console.warn('No refresh token available, redirecting to login...');
          localStorage.clear();
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect
        console.warn('Token refresh failed, redirecting to login...');
        localStorage.clear();
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }

    return Promise.reject(error);
  }
);

export default api;